import {I18n} from 'aws-amplify/utils';

// FNP-1439 — no `productCopy` import: the one sentence in this panel that
// carried the {product} token was cut, and nothing left on screen names the
// product. Restore it alongside any new copy that does.
import {
    Alert,
    Anchor,
    Code,
    Group,
    List,
    Stack,
    Text,
} from '@mantine/core';
import {FlexiCopyButton} from '@flexinet/ui-components';

import {ProviderConnection} from '../types';
import {CONNECT_PROVIDER_DOCS_URL} from '../config';
import {
    presentStatus,
    rendersExternalIdPanel,
} from '../statusPresentation';
import {isConnectionUsable} from '../types';
import {
    declaredSelectionOf,
    permissionPolicyFor,
    trustPolicyFor,
} from '../services/rolePolicyBuilder';
import {useFlexiPrincipalQuery} from '../hooks/useProviderConnectionsQuery';

// FNP-1248 — THE EXTERNAL ID, AND WHY THE CONNECTION IS NOT ACTIVE YET.
//
// ⚠️ THIS COMPONENT IS THE POINT OF THE WHOLE MODULE AT STEP 2 OF 3.
//
// The ordering problem it exists to solve:
//
//   Flexi mints a per-connection external id SERVER-SIDE during
//   `connectProvider`, via issueExternalId(connectionId). It is DERIVED FROM
//   THE CONNECTION ID, so it cannot exist before the connection does. The
//   tenant needs it in their role's trust policy before Flexi can assume the
//   role. Therefore:
//
//     1. Connect      → connection stored, status INVALID/UNREACHABLE,
//                       secretRef.externalId now populated.
//     2. THIS PANEL   → show the external id + role ARN so the tenant can add
//                       them to their trust policy.
//     3. Re-validate  → rotateProviderCredential with the same roleArn.
//
// Step 1 coming back not-ACTIVE is the DESIGNED path. There is no ordering in
// which a first Route 53 connection succeeds immediately, and no amount of
// server-side cleverness can produce one — the id is a function of the record.
//
// ⚠️ `testProviderCredential` CANNOT SHORTCUT THIS. The external id it mints is
// a throwaway: never stored, never returned. A tenant cannot put it in a trust
// policy, so it can never make an assumption succeed. Nothing in this module
// implies otherwise.
//
// ⚠️ WHAT IS SHOWN AND WHAT IS NOT.
//   externalId — SHOWN, prominently, copyable. Flexi must TELL the tenant this.
//   roleArn    — SHOWN when known. An identifier, not a secret; the tenant
//                needs it to find the right role.
//   secretArn  — NEVER. Infrastructure detail with no tenant audience; it is
//                not even selected in this module's queries.
//   apiToken   — NEVER, and structurally cannot be: no output type carries it.

export const ExternalIdPanel = (
    {
        connection,
        roleArn,
    }: {
        connection: ProviderConnection,
        // The role ARN the tenant submitted, when the caller still has it in
        // hand — right after a connect or rotate. It is NOT stored on the
        // connection record, so on a page load it is legitimately absent and
        // the panel simply omits that line rather than inventing one.
        roleArn?: string,
    },
) => {

    const externalId = connection.secretRef?.externalId;

    // ⚠️ FNP-1469 — READ BEFORE THE EARLY RETURN. Hooks cannot be called
    // conditionally, and the guard below returns for API_TOKEN connections.
    // The query is keyed without an id and cached hard, so this costs nothing
    // on the paths that do not use it.
    const {data: flexiPrincipal} = useFlexiPrincipalQuery();

    // No external id means this is not the ASSUMED_ROLE trust-policy flow —
    // an API_TOKEN connection, or an ASSUMED_ROLE one the API did not mint an
    // id for. Either way there is nothing here to tell the tenant to do.
    //
    // ⚠️ GATED ON THE SHARED HELPER, not on a second reading of `secretRef`.
    // The status card and the connect drawer both stand their own explanation
    // down when `rendersExternalIdPanel` says this panel will render (FNP-1286).
    // If that helper and this early return ever disagreed, a state would be
    // explained twice or not at all — so they are the same predicate, not two
    // copies of one rule. `externalId` is still narrowed below for the type.
    if (!rendersExternalIdPanel(connection) || !externalId) {
        return null;
    }

    const presentation = presentStatus(connection);

    // FNP-1469 — the two documents the steps above tell the tenant to write.
    // Both are derived, never stored: the permission policy from the grants
    // this connection declared, the trust policy from Flexi's principal and
    // this connection's own external ID.
    const permissionPolicy = permissionPolicyFor(declaredSelectionOf(connection));
    const trustPolicy = trustPolicyFor(flexiPrincipal?.arn ?? '', externalId);

    return (
        <Alert
            data-testid={'external-id-panel'}
            // ⚠️ BLUE FOR THE EXPECTED STEP, not red. This is an instruction,
            // not a failure report. A red panel here tells the tenant they made
            // a mistake at the exact moment they need to calmly copy a value.
            color={presentation.isExpectedNextStep ? 'blue' : 'gray'}
            variant={'light'}
            title={presentation.isExpectedNextStep
                ? I18n.get('Add this external ID to your trust policy')
                : I18n.get('External ID for this connection')}
        >
            <Stack gap={'md'}>

                {/* ⚠️ FNP-1286 — THE SUMMARY RENDERS HERE, AND ONLY HERE, FOR
                    EVERY STATE THIS PANEL IS ON SCREEN FOR.
                    The detail view's status card used to render it too, so the
                    tenant read the same paragraph twice. The card now stands
                    down whenever this panel renders, which means this panel
                    must carry the sentence in EVERY state, not just the
                    awaiting-trust-policy one — an ACTIVE connection keeps its
                    external id, so this panel renders for it as well, and
                    dropping the summary would leave that state explained by
                    nothing but a badge.

                    `presentation.summary` rather than a paraphrase: the one
                    sentence that stops a tenant reading INVALID as their own
                    mistake is then the same sentence everywhere it appears, and
                    cannot drift between here and the table. */}
                <Text size={'sm'} fw={presentation.isExpectedNextStep ? 500 : undefined}>
                    {presentation.summary}
                </Text>

                {/* ⚠️ FNP-1439 — NO SENTENCE HERE. The alert title above IS the
                    instruction ("Add this external ID to your trust policy"),
                    and the numbered steps below are the detail. A paragraph in
                    between restated both.

                    What it used to say, kept because it is the question a
                    tenant actually asks: the connection stays inactive until
                    the ID is in the trust policy. The ID is derived from the
                    connection, so it cannot exist before the connection does —
                    which is why this panel appears after saving rather than
                    during the connect form. `presentation.summary` above
                    already tells them the connection is not active yet, so the
                    screen loses nothing by carrying the reason only here. */}

                {/* ─── THE VALUE THE TENANT CAME FOR ─── */}
                <Stack gap={4}>
                    <Text size={'xs'} c={'dimmed'} fw={500}>
                        {I18n.get('External ID')}
                    </Text>
                    <Group gap={'xs'} wrap={'nowrap'}>
                        <Code data-testid={'external-id'}>{externalId}</Code>
                        {/* Copyable because it is a long opaque string that
                            must be transcribed EXACTLY into a trust policy.
                            Retyping it by hand is the likeliest way this flow
                            fails for a reason nobody can see. */}
                        {/* `data-testid` because this is an icon-only
                            ActionIcon inside a Tooltip: the "Copy" label lives
                            on the tooltip, which gives the button no accessible
                            name in jsdom, so `getByRole('button', {name: /copy/})`
                            cannot find it. The testid is how a test proves the
                            copy control survived (FNP-1286). */}
                        <FlexiCopyButton value={externalId} data-testid={'external-id-copy'}/>
                    </Group>
                </Stack>

                {roleArn && (
                    <Stack gap={4}>
                        <Text size={'xs'} c={'dimmed'} fw={500}>
                            {I18n.get('Role ARN')}
                        </Text>
                        <Group gap={'xs'} wrap={'nowrap'}>
                            <Code data-testid={'role-arn'}>{roleArn}</Code>
                            <FlexiCopyButton value={roleArn}/>
                        </Group>
                    </Stack>
                )}

                {presentation.isExpectedNextStep && (
                    <Stack gap={4}>
                        <Text size={'sm'} fw={500}>{I18n.get('What to do')}</Text>
                        <List size={'sm'} spacing={4}>
                            <List.Item>
                                {I18n.get('Open the IAM role in your own AWS account.')}
                            </List.Item>
                            <List.Item>
                                {I18n.get('Set its sts:ExternalId condition to the ID above.')}
                            </List.Item>
                            {/* ⚠️ FNP-1276 — THE PERMISSION POLICY, NOT ONLY THE
                                TRUST POLICY.

                                The trust policy decides WHO may assume the role.
                                It says nothing about what the role may then do,
                                and a tenant who fixes only the external id ends
                                up with a connection that assumes cleanly and
                                still cannot issue a certificate.

                                Decision 5 of docs/decisions/certificate-entity-model.md
                                puts the ACM grants in THIS role, deliberately:
                                the certificate is the tenant's asset, issued in
                                the tenant's account. Flexi holds no ACM write
                                grant anywhere, so there is nothing on Flexi's
                                side to fall back to.

                                The tenant is inside the role right now. This is
                                the cheapest moment in the whole flow to widen
                                the permission policy, and the only one before
                                the first request fails. */}
                            <List.Item>
                                {/* Certificates are issued in the tenant's own
                                    account under this credential, which is why
                                    the ACM actions belong on their role rather
                                    than ours. */}
                                {I18n.get('For certificates, also add acm:RequestCertificate and acm:DeleteCertificate.')}
                            </List.Item>
                            <List.Item>
                                {/* ⚠️ FNP-1439 — "the ID does not change, so this
                                    is a one-off" is gone from the screen. It
                                    reassures, but it answers a question the
                                    tenant has not asked yet at step 4 of 4. */}
                                {I18n.get('Come back here and choose Re-validate.')}
                            </List.Item>
                        </List>
                        {/* ⚠️ THE GRANTS MUST OUTLIVE THE REQUEST.
                            AWS re-checks the DNS validation record for the life
                            of the certificate, so both the Route 53 write grant
                            and the ACM grants have to stay for years. A tenant
                            tightening the role months later breaks renewal, and
                            nothing links the two events — recorded as a real
                            trap in the decision doc's "Still open" section. It
                            is worth its own line rather than a clause tacked on
                            to a step the tenant has already ticked off. */}
                        <Text size={'xs'} c={'dimmed'} mt={4}>
                            {/* AWS re-checks the validation record for the whole life of the
                                certificate, so a permission removed months later breaks
                                renewal with no signal at the time of the change. */}
                            {I18n.get('Leave the permissions in place — removing them later breaks certificate renewal.')}
                        </Text>
                        {/* FNP-1301 — the policies in full, as a page that can
                            be read before this panel or handed to whoever
                            administers the tenant's AWS account. */}
                        <Anchor
                            size={'xs'}
                            href={CONNECT_PROVIDER_DOCS_URL}
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                            mt={4}
                        >
                            {I18n.get('The full trust policy and permission policy')}
                        </Anchor>
                    </Stack>
                )}

                {/* ─── FNP-1469: THE DOCUMENTS, WHERE THEY ARE NEEDED ───
                    Both policies rendered only on the PRE-SAVE form, inside
                    `RoleBuilderPanel`, which is unreachable once the connection
                    exists. That panel even promises this one will carry them —
                    "Shown on the connection once you save it." — and it did not.

                    The external ID is derived from the connection, so a tenant
                    CANNOT have written the trust policy before arriving here.
                    The only other route was a link that 404s (FNP-1468).

                    ⚠️ OUTSIDE `isExpectedNextStep`, DELIBERATELY. That flag is
                    false once the provider has refused the credential, because
                    FNP-1285 stops the panel instructing a tenant whose role ARN
                    is simply wrong. But STS answers AccessDenied identically
                    whether the role is absent or merely does not trust Flexi
                    yet — the product cannot tell them apart, and says so. So a
                    refused connection is exactly as likely to need this trust
                    policy, and gating the documents on that flag would hide
                    them in the state where they are most needed. Rendering them
                    instructs nobody; it answers "what am I supposed to paste?"

                    Not shown for an ACTIVE connection: `rendersExternalIdPanel`
                    keeps this panel alive for one, and a working connection is
                    not being asked to write anything. */}
                {!isConnectionUsable(connection) && (
                    <Stack gap={'md'}>
                        <Stack gap={'xs'}>
                            <Group justify={'space-between'}>
                                <Text size={'sm'} fw={500}>
                                    {I18n.get('Your permission policy')}
                                </Text>
                                <FlexiCopyButton
                                    value={permissionPolicy}
                                    data-testid={'connection-permission-policy-copy'}
                                />
                            </Group>
                            {/* ⚠️ BUILT FROM WHAT THIS CONNECTION DECLARED, not
                                from DEFAULT_GRANTS. A tenant who ticked only
                                record writes must not be handed a document
                                granting zone creation — it would disagree with
                                their own checkboxes and with the adapter. */}
                            <Code block data-testid={'connection-permission-policy'}>
                                {permissionPolicy}
                            </Code>
                        </Stack>

                        {/* ⚠️ NO PRINCIPAL, NO TRUST POLICY — the same refusal
                            `RoleBuilderPanel` makes, for the same reason: a
                            policy naming the wrong principal produces a role
                            that cannot be assumed and an error that blames the
                            tenant's account. The permission policy above does
                            not depend on the principal and still renders. */}
                        {flexiPrincipal?.arn && (
                            <Stack gap={'xs'}>
                                <Group justify={'space-between'}>
                                    <Text size={'sm'} fw={500}>
                                        {I18n.get('Your trust policy')}
                                    </Text>
                                    <FlexiCopyButton
                                        value={trustPolicy}
                                        data-testid={'connection-trust-policy-copy'}
                                    />
                                </Group>
                                {/* ⚠️ THE REAL EXTERNAL ID, SUBSTITUTED. Called
                                    without one, `trustPolicyFor` renders the
                                    "<the external ID … after you save>"
                                    placeholder — correct before the save, and
                                    the same defect after it. */}
                                <Code block data-testid={'connection-trust-policy'}>
                                    {trustPolicy}
                                </Code>
                            </Stack>
                        )}
                    </Stack>
                )}

                {/* The provider's own words, VERBATIM, when there are any.
                    Never re-worded — classify for the core, preserve for the
                    human (FNP-1180). Kept dimmed and below the instruction: it
                    is corroborating detail here, not the headline. */}
                {connection.statusDetail && (
                    <Stack gap={4}>
                        <Text size={'xs'} c={'dimmed'} fw={500}>
                            {I18n.get('Provider response')}
                        </Text>
                        <Text size={'xs'} c={'dimmed'}>
                            {connection.statusDetail}
                        </Text>
                    </Stack>
                )}

            </Stack>
        </Alert>
    );
};
